import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Download } from 'lucide-react';
import ModernTimeDropdown from '../../../dashboard-components/layouts/modernDropdown';
import server from '../../../api/server';
import { TrendsLineWithMetrics, PieChart2, DualAxisBarLineChart } from '../../../dashboard-components/layouts/ChartLayout';
import ConversationRecords from '../calls/ConversationRecords';
import { FaTimes } from 'react-icons/fa';
import { FaFilter } from 'react-icons/fa';
import { FaCaretDown, FaCaretRight } from 'react-icons/fa';
import ModernDatePicker from '../../../dashboard-components/layouts/ModernDatePicker';
import {
  initializeFiltersFromUrl,
  updateUrlWithFilters,
  shouldHideCrmLeadCreatedAtChipKey,
  stripCrmLeadCreatedAtFromAdvancedFilters,
} from '../../../utils/filterUtils';

const CONNECTED_SLOT_OPTIONS = [
  { key: 'slot_30_minutes', label: '30 mins' },
  { key: 'slot_1_hour', label: '1 hr' },
  { key: 'slot_3_hours', label: '3 hrs' },
];

const HomePage = ({ clientID, selectedOptions }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loadingTrendLine, setLoadingTrendLine] = useState(false)
  const [loadingPie, setLoadingPie] = useState(false)
  const [client_id, setClient_id] = useState(null)
  const [email, setEmail] = useState(null)
  const [metricsDistribution, setMetricsDistribution] = useState(null)
  const [metricsData, setMetricsData] = useState(null)
  const [activeMetric, setActiveMetric] = useState('calls')
  const [showDetails, setShowDetails] = useState(false)
  const [selectedSegment, setSelectedSegment] = useState(null)
  const [disconnectReasonsData, setDisconnectReasonsData] = useState(null)
  const [disconnectTypesData, setDisconnectTypesData] = useState(null)
  const [businessInsightsData, setBusinessInsightsData] = useState(null)
  const [loadingBusinessInsights, setLoadingBusinessInsights] = useState(false)
  const [transferStatusData, setTransferStatusData] = useState(null)
  const [transferAgentAnsweredData, setTransferAgentAnsweredData] = useState(null)
  const [crmUpdateStatusData, setCrmUpdateStatusData] = useState(null)
  const [callDetailsMetricsTable, setCallDetailsMetricsTable] = useState(null)
  const [customerDetailsMetricsTable, setCustomerDetailsMetricsTable] = useState(null)
  const [connectedCallsSlotData, setConnectedCallsSlotData] = useState(null)
  const [connectedSlotType, setConnectedSlotType] = useState('slot_3_hours')
  // Mock data for analytics charts
  const defaultFilters = {
    conversation_id: '',
    agent: '',
    time: 'Last 7 Days',
    customStart: '',
    customEnd: '',
    mode: 'Live Conversation'
  };
  const defaultAdvancedFilters = {
    type: '',
    customer_id: '',
    audioDuration: '',
    conversation_direction: '',
    disconnect_type: '',
    disconnect_reason: '',
    average_latency: '',
    campaign_id: '',
    campaign_batch_id: '',
    transfer_status: '',
    transfer_agent_answered: '',
    crm_update_status: '',
    agent_feedback_multiple: [],
    business_insights_multiple: []
  };
  const initialFilterState = initializeFiltersFromUrl(location, defaultFilters, defaultAdvancedFilters);
  const [filters, setFilters] = useState(initialFilterState.filters);
  const [advancedFilters, setAdvancedFilters] = useState(initialFilterState.advancedFilters);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    businessInsights: false
  });
  const [service_id, setService_id] = useState(null)

  const [dummyFilterData, setDummyFilterData] = useState({
    agents: [],
    time: ['Today',
      'Yesterday',
      'This Week',
      "This Month",
      "Last Month",
      "Last 7 Days",
      "Last 30 Days",
      "Last 3 Months",
      "Custom Range"],
    modes: ['Live Conversation', 'Test Conversation']
  })
  const [dummyAdvancedFilterData, setDummyAdvancedFilterData] = useState({
    audioDuration: ['Less than 1 Minute', '1-2 Minutes', '3-5 Minutes', 'Above 2 Minutes', 'More than 5 Minutes'],
    disconnect_types: [],
    disconnect_reasons: [],
    conversation_directions: [],
    campaign_ids: [],
    campaign_batch_ids: [],
    transfer_statuses: [],
    transfer_agent_answereds: [],
    business_insights: []
  });

  function getCookie(name) {
    function escape(s) { return s.replace(/([.*+?\^$(){}|\[\]\/\\])/g, '\\$1'); }
    var match = document.cookie.match(RegExp('(?:^|;\\s*)' + escape(name) + '=([^;]*)'));
    return match ? match[1] : null;
  }

  const handleAdvancedInputChange = (e, key) => {
    const value = e.target.value;
    setAdvancedFilters((prev) => ({ ...prev, [key]: value }));
  };
  const handleQuickFilterChange = (e, key) => {
    const value = e.target.value;
    if (key === 'time' && value !== 'Custom Range') {
      setFilters((prev) => ({
        ...prev,
        [key]: value,
        customStart: '',
        customEnd: ''
      }));
      return;
    }
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const updateBusinessInsights = (index, field, value) => {
    setAdvancedFilters((prev) => ({
      ...prev,
      business_insights_multiple: prev.business_insights_multiple.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const getBusinessInsightKeys = () => {
    const src = dummyAdvancedFilterData.business_insights;
    if (!src) return [];
    if (Array.isArray(src)) {
      if (src.length > 0 && Array.isArray(src[0])) {
        return src.map(([k]) => k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()));
      }
      return src.map((k) => String(k).replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()));
    }
    if (typeof src === 'object') {
      return Object.keys(src).map((k) => k.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()));
    }
    return [];
  };

  const getBusinessInsightValues = (selectedKeyDisplay) => {
    const src = dummyAdvancedFilterData.business_insights;
    if (!src || !selectedKeyDisplay) return [];
    const keySnake = selectedKeyDisplay.toLowerCase().replace(/ /g, '_');
    if (Array.isArray(src)) {
      if (src.length > 0 && Array.isArray(src[0])) {
        const found = src.find(([k]) => k === keySnake || k.replace(/_/g, ' ').toLowerCase() === selectedKeyDisplay.toLowerCase());
        return found ? (Array.isArray(found[1]) ? found[1] : []) : [];
      }
      return [];
    }
    if (typeof src === 'object') {
      return Array.isArray(src[keySnake]) ? src[keySnake] : [];
    }
    return [];
  };

  const getMetricsData = async()=> {
    try {
      setLoadingTrendLine(true)
      setLoadingPie(true)
      setLoadingBusinessInsights(true)
      const advancedPayload = [
        advancedFilters.audioDuration,
        advancedFilters.conversation_direction,
        advancedFilters.customer_id,
        advancedFilters.disconnect_type,
        advancedFilters.disconnect_reason,
        advancedFilters.average_latency,
        advancedFilters.campaign_id,
        advancedFilters.campaign_batch_id,
        advancedFilters.transfer_status,
        advancedFilters.transfer_agent_answered,
        JSON.stringify(advancedFilters.business_insights_multiple || []),
        advancedFilters.crm_update_status
      ];
      let res = await server.getVoiceAgentsOverallMetrics(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      let res2 = await server.getVoiceAgentsMetricsDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res.status === 200){
        let resp = await res.json()
        let resp2 = await res2.json()
        setMetricsDistribution(resp2.data)
        setMetricsData(transformMetrics(resp.data))
      } else {
        setMetricsDistribution(null)
        setMetricsData(null)
      }
      setLoadingTrendLine(false)
      let res3 = await server.getVoiceAgentDisconnectReasonsDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res3.status === 200){
        let resp3 = await res3.json()
        setDisconnectReasonsData(resp3.data)
      }
      let res4 = await server.getVoiceAgentDisconnectTypesDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res4.status === 200){
        let resp4 = await res4.json()
        setDisconnectTypesData(resp4.data)
      }
      let res5 = await server.getVoiceAgentBusinessInsights(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res5.status === 200){
        let resp5 = await res5.json()
        setBusinessInsightsData(resp5.data)
      } else {
        setBusinessInsightsData(null)
      }
      setLoadingBusinessInsights(false)
      let res6 = await server.getVoiceAgentTransferStatusDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res6.status === 200){
        let resp6 = await res6.json()
        setTransferStatusData(resp6.data)
      }
      let res7 = await server.getVoiceAgentTransferAgentAnsweredDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res7.status === 200){
        let resp7 = await res7.json()
        setTransferAgentAnsweredData(resp7.data)
      }
      let res8 = await server.getVoiceAgentCrmUpdateStatusDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res8.status === 200){
        let resp8 = await res8.json()
        setCrmUpdateStatusData(resp8.data)
      }
      let res9 = await server.getVoiceAgentCallDetailsMetricsDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res9.status === 200){
        let resp9 = await res9.json()
        setCallDetailsMetricsTable(resp9.data)
      } else {
        setCallDetailsMetricsTable(null)
      }
      let res10 = await server.getVoiceAgentCustomerDetailsMetricsDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res10.status === 200){
        let resp10 = await res10.json()
        setCustomerDetailsMetricsTable(resp10.data)
      } else {
        setCustomerDetailsMetricsTable(null)
      }
      let res11 = await server.getConnectedCallsSlotDistribution(client_id, '', email, 'voice-agents', 'home', filters.conversation_id, filters.agent, filters.time, filters.customStart, filters.customEnd, filters.mode, ...advancedPayload)
      if(res11.status === 200){
        let resp11 = await res11.json()
        setConnectedCallsSlotData(resp11.data)
      } else {
        setConnectedCallsSlotData(null)
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoadingTrendLine(false)
      setLoadingPie(false)
      setLoadingBusinessInsights(false)
    }
  }

  const getFiltersData = async()=> {
    try {
      let res = await server.getVoiceAgentsMetricsFilters(client_id, '', email, 'voice-agents', 'home')
      if(res.status === 200){
        let resp = await res.json()
        setDummyFilterData((prev) => ({ ...prev, agents: resp.agents || [] }))
        setDummyAdvancedFilterData((prev) => ({
          ...prev,
          disconnect_types: resp.disconnect_types || [],
          disconnect_reasons: resp.disconnect_reasons || [],
          conversation_directions: resp.conversation_directions || [],
          campaign_ids: resp.campaign_ids || [],
          campaign_batch_ids: resp.campaign_batch_ids || [],
          transfer_statuses: resp.transfer_statuses || [],
          transfer_agent_answereds: resp.transfer_agent_answered || [],
          business_insights: resp.business_insights || []
        }))
      }
    } catch (error) {
      console.log(error)
    }
  }

  useEffect(()=> {
    if(client_id && email){
      getMetricsData()
      getFiltersData()
    }
  }, [client_id, email, filters, advancedFilters])

  // Sync filters to URL with debounce
  useEffect(() => {
    if (!client_id) return;
    const timeoutId = setTimeout(() => {
      updateUrlWithFilters(
        navigate,
        location,
        filters,
        stripCrmLeadCreatedAtFromAdvancedFilters(advancedFilters),
        filters.customStart,
        filters.customEnd
      );
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters, advancedFilters, client_id]);

  useEffect(()=> {
    if(clientID){
      setClient_id(clientID)
    }
    if(selectedOptions){
      setService_id(selectedOptions)
    }
    const user = getCookie('dashboard_login_user_creds') ? JSON.parse(getCookie('dashboard_login_user_creds')) : null;
    if (user) {
      setEmail(user.email);
    }
  }, [clientID, selectedOptions])

  useEffect(() => {
    if (!connectedCallsSlotData || typeof connectedCallsSlotData !== 'object') return;
    if (Array.isArray(connectedCallsSlotData[connectedSlotType])) return;
    const next = CONNECTED_SLOT_OPTIONS.find((o) => Array.isArray(connectedCallsSlotData[o.key]));
    if (next) setConnectedSlotType(next.key);
  }, [connectedCallsSlotData, connectedSlotType]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') setShowAdvancedFilters(false);
    };
    if (showAdvancedFilters) {
      window.addEventListener('keydown', handleEsc);
    }
    return () => window.removeEventListener('keydown', handleEsc);
  }, [showAdvancedFilters]);

  const connectedSlotChartRows = useMemo(() => {
    const raw = connectedCallsSlotData?.[connectedSlotType];
    if (!Array.isArray(raw)) return [];
    return raw.map((item) => {
      const totalCalls = Number(item?.total_calls);
      const connectedPct = Number(item?.connected_calls_percentage);
      const slotStart = item?.slot_start ?? item?.slot_label ?? item?.slot ?? item?.duration_slot ?? item?.duration ?? '-';
      const slotEnd = item?.slot_end ?? '';
      const slotKey = item?.slot_key ?? (slotEnd ? `${slotStart}-${slotEnd}` : slotStart);
      return {
        slot_start: slotStart,
        slot_end: slotEnd,
        slot_key: slotKey,
        total_calls: Number.isFinite(totalCalls) ? totalCalls : 0,
        connected_calls_percentage: Number.isFinite(connectedPct) ? connectedPct : 0,
      };
    }).sort((a, b) => String(a.slot_start).localeCompare(String(b.slot_start), undefined, { numeric: true }));
  }, [connectedCallsSlotData, connectedSlotType]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
  
    if (hrs > 0) {
      return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    } else {
      return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
  };

  /** HH:MM:SS for metrics distribution tables (Total Duration, AHT). Seconds may be fractional (e.g. AHT). */
  const formatSecondsAsHHMMSS = (seconds) => {
    const s = Number(seconds);
    if (!Number.isFinite(s)) return String(seconds);
    const sign = s < 0 ? '-' : '';
    const totalSecs = Math.round(Math.abs(s));
    const hrs = Math.floor(totalSecs / 3600);
    const mins = Math.floor((totalSecs % 3600) / 60);
    const secs = totalSecs % 60;
    return `${sign}${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  /**
   * Shape: first column is `metric`, remaining columns are dates (or other series keys).
   * Duration formatting applies to every non-metric cell when row[metric] is Total Duration or AHT.
   */
  const findMetricColumnKey = (columns) => {
    if (!Array.isArray(columns) || columns.length === 0) return null;
    const byExact = columns.find((c) => String(c).trim().toLowerCase() === 'metric');
    if (byExact) return byExact;
    return columns.find((c) => /^metric$/i.test(String(c).trim())) ?? null;
  };

  const isDurationMetricLabel = (label) => {
    const n = String(label ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
    if (!n) return false;
    if (n === 'aht') return true;
    if (n === 'total duration') return true;
    if (n.includes('total duration')) return true;
    if (n.includes('average handle time')) return true;
    return false;
  };

  const shouldFormatDurationMetricCell = (columnKey, row, columns) => {
    const metricCol = findMetricColumnKey(columns);
    if (!metricCol || columnKey === metricCol) return false;
    return isDurationMetricLabel(row?.[metricCol]);
  };

  const formatMetricsCellForDisplay = (columnName, value, row, columns) => {
    if (value == null) return '-';
    if (shouldFormatDurationMetricCell(columnName, row, columns)) {
      const n = Number(value);
      if (Number.isFinite(n)) return formatSecondsAsHHMMSS(n);
    }
    return String(value);
  };

  const escapeCsvCell = (value) => {
    if (value === null || value === undefined) return '';
    const str = String(value);
    if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
      return `"${str.replace(/"/g, '""')}"`;
    }
    return str;
  };

  const handleDownloadMetricsDistributionTable = (tableData, title) => {
    const columns = Array.isArray(tableData?.columns) ? tableData.columns : [];
    const rows = Array.isArray(tableData?.rows) ? tableData.rows : [];
    if (columns.length === 0 || rows.length === 0) return;

    let csvContent = columns.map((col) => escapeCsvCell(col)).join(',') + '\n';
    rows.forEach((row) => {
      const line = columns.map((col) => {
        const raw = row?.[col];
        let cell;
        if (raw == null || raw === '') cell = '';
        else if (shouldFormatDurationMetricCell(col, row, columns)) {
          const n = Number(raw);
          cell = Number.isFinite(n) ? formatSecondsAsHHMMSS(n) : String(raw);
        } else {
          cell = String(raw);
        }
        return escapeCsvCell(cell);
      }).join(',');
      csvContent += line + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const safeTitle = String(title).replace(/[^\w\s-]/g, '').replace(/\s+/g, '_').slice(0, 80);
    const fileName = `${safeTitle}_${new Date().toISOString().slice(0, 10)}.csv`;
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const handleShowCalls = (data) => {
    data['filter_applied'] = {
      ...filters,
      ...advancedFilters,
      record_type: data.id === 'chats' ? 'chat' : 'call',
      customStart: filters.customStart,
      customEnd: filters.customEnd
    };
    setSelectedSegment(data)
    console.log(data)
    setShowDetails(true);
  }

  const handleSegmentClick = (data, type) => {
    console.log(data)
    data['filter_applied'] = {
      ...filters,
      ...advancedFilters,
      customStart: filters.customStart,
      customEnd: filters.customEnd,
      [type]: data.name===null ? 'NA' : data.name
    };
    setSelectedSegment(data);
    console.log(data)
    setShowDetails(true);
  }
  // Business Insights pie click -> open modal with BI filters
  const handleBusinessInsightSegmentClick = (data, key) => {
    const biKey = key;
    const biValue = data?.name === null ? 'NA' : data?.name;
    const selectedBusinessInsightFilter = {
      business_insight_key: biKey,
      business_insight_value: biValue
    };
    const existingBusinessInsightFilters = Array.isArray(advancedFilters.business_insights_multiple)
      ? advancedFilters.business_insights_multiple.filter((item) => item?.business_insight_key !== biKey)
      : [];
    const enriched = {
      ...data,
      filter_applied: {
        ...filters,
        ...advancedFilters,
        customStart: filters.customStart,
        customEnd: filters.customEnd,
        business_insights_multiple: [
          ...existingBusinessInsightFilters,
          selectedBusinessInsightFilter
        ],
        business_insight_key: biKey,
        business_insight_value: biValue
      }
    };
    setSelectedSegment(enriched);
    setShowDetails(true);
  };

  const handleDownload = (data, title) => {
    const csvContent = "data:text/csv;charset=utf-8," + data.map(row => Object.values(row).join(",")).join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `${title}_data.csv`);
    document.body.appendChild(link);
    link.click();
  }

  const transformMetrics = (data) => {
    const lastData = data['last_current_period'];
    const prevData = data['percentage_change']
    let transformedData = [
      {
        id: 'calls',
        title: '#Calls',
        value: lastData.total_calls,
        change: prevData.total_calls,
        color: '#4F46E5',
        allowVisit: true,
        formatter: (value) => value
      },
      {
        id: 'chats', title: '#Chats', value: lastData.total_chats ?? 0,
        change: prevData.total_chats ?? 0, color: '#4F46E5', allowVisit: true,
        formatter: value => value,
      },
      {
        id: 'duration',
        title: '#Duration',
        value: lastData.total_duration,
        change: prevData.total_duration,
        color: '#4F46E5',
        allowVisit: false,
        formatter: (value) => formatTime(value)
      },
      {
        id: 'billed_duration',
        title: 'Billed Duration',
        value: lastData.total_billed_duration,
        change: prevData.total_billed_duration,
        color: '#4F46E5',
        allowVisit: false,
        formatter: (value) => formatTime(value)
      },
      {
        id: 'aht',
        title: 'AHT',
        value: lastData.aht,
        change: prevData.aht,
        color: '#4F46E5',
        allowVisit: false,
        formatter: (value) => formatTime(value)
      },
      {
        id: 'connected_calls_percentage',
        title: 'Connected Calls %',
        value: parseInt(lastData.connected_calls_percentage || 0),
        change: prevData.connected_calls_percentage,
        color: '#4F46E5',
        allowVisit: false,
        formatter: (value) => value
      },
      {
        id: 'proper_calls_percentage',
        title: 'Proper Calls %',
        value: parseInt(lastData.proper_calls_percentage || 0),
        change: prevData.proper_calls_percentage,
        color: '#4F46E5',
        allowVisit: false,
        formatter: (value) => value
      }
    ];
    return transformedData
  };
  const PieChartsGrid = ({data}) => {
    return (
      
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(data).map(([key, value], index) => (
                <div key={index} className="col-span-1 rounded-md" style={{background: "white", minHeight: "25rem"}}>
                    {
                        loadingBusinessInsights && (
                            <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                            <div className="loader"></div>
                            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
                            </div>
                        )
                        }
                  {!loadingBusinessInsights && value.length < 10 && <PieChart2 
                    onSegmentClick={(d) => handleBusinessInsightSegmentClick(d, key)} 
                    pieData={value} 
                    width="w-full" 
                    colors={['#0088FE', '#00C49F', '#FFBB28', '#FF8042']} 
                    title={key}  
                    handleDownload={handleDownload}
                  />}
                  {
                    !loadingBusinessInsights && value.length >= 10 && (
                      <div className="overflow-x-auto px-3" style={{height: "28rem", overflowY: "auto"}}>
                        <div className="flex justify-between items-center sticky top-0 left-0 w-full bg-white">
                          <div className="text-xl font-semibold ml-3 mt-3 mb-3">{key}</div>
                          <div><Download className="text-gray-500 hover:text-gray-700 cursor-pointer mr-3 mt-3 mb-3" size={20} onClick={()=>handleDownload(value, key)} /></div>
                        </div>
                        <table className="min-w-full">
                    <thead>
                    <tr className="bg-gray-50 text-left">
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                    {value.map((item, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm text-gray-900">{item['name']}</td>
                        <td onClick={()=>handleBusinessInsightSegmentClick(item, key)} className="px-4 py-3 text-sm text-gray-900 cursor-pointer text-indigo-500 hover:text-indigo-700 underline">{item['value']}</td>
                        </tr>
                    ))}
                    </tbody>
                </table>
                      </div>
                    )
                  }
                </div>
              ))}
            </div>
    );
  };

  const MetricsDistributionTable = ({ title, tableData, loading }) => {
    const columns = Array.isArray(tableData?.columns) ? tableData.columns : [];
    const rows = Array.isArray(tableData?.rows) ? tableData.rows : [];
    const hasData = columns.length > 0 && rows.length > 0;
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 mt-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">{title}</h2>
          {!loading && hasData && (
            <button
              type="button"
              onClick={() => handleDownloadMetricsDistributionTable(tableData, title)}
              className="flex items-center p-1 rounded hover:bg-gray-100"
              aria-label={`Download ${title} as CSV`}
            >
              <Download className="text-gray-500 hover:text-gray-700 cursor-pointer" size={20} />
            </button>
          )}
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-16" style={{ flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{ textAlign: "center" }}>Loading <br /> Please Wait</div>
          </div>
        ) : !hasData ? (
          <div className="text-sm text-gray-500">No data available</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {columns.map((col, idx) => (
                    <th key={`${title}-col-${idx}`} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {rows.map((row, rowIdx) => (
                  <tr key={`${title}-row-${rowIdx}`} className="hover:bg-gray-50">
                    {columns.map((col, colIdx) => (
                      <td key={`${title}-row-${rowIdx}-col-${colIdx}`} className="px-4 py-3 text-sm text-gray-900">
                        {formatMetricsCellForDisplay(col, row?.[col], row, columns)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="py-3 px-5 font-sans bg-gray-50">
      <div className='mb-4'>
        <h1 className="text-xl font-semibold text-gray-900 mb-2">Home</h1>
        <p className="text-sm text-gray-500">View and manage all voice agents</p>
      </div>
      {/* Filters - Quick Filters style */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-medium text-gray-700">Quick Filters</div>
          <button
            type="button"
            className="flex items-center text-indigo-600 text-sm"
            onClick={() => setShowAdvancedFilters((prev) => !prev)}
          >
            <FaFilter className="mr-2" />
            {showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
          </button>
        </div>
        <div className="flex items-center gap-4 flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
            <ModernTimeDropdown
              placeholder="Select Agent"
              value={filters.agent}
              onChange={(e) => handleQuickFilterChange(e, 'agent')}
              options={dummyFilterData.agents}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
            <ModernTimeDropdown
              placeholder="Select Date Range"
              value={filters.time}
              onChange={(e) => handleQuickFilterChange(e, 'time')}
              options={dummyFilterData.time}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Audio Duration</label>
            <ModernTimeDropdown
              placeholder="Select Duration"
              value={advancedFilters.audioDuration}
              onChange={(e) => handleAdvancedInputChange(e, 'audioDuration')}
              options={dummyAdvancedFilterData.audioDuration}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
            <ModernTimeDropdown
              placeholder="Select Mode"
              value={filters.mode}
              onChange={(e) => handleQuickFilterChange(e, 'mode')}
              options={dummyFilterData.modes}
            />
          </div>
        </div>
        {filters.time === "Custom Range" && (
            <div className="flex gap-4 mb-2">
              <div>
                <ModernDatePicker
                  placeholder="Start Date"
                  value={filters.customStart}
                  onChange={(e)=>handleQuickFilterChange(e, 'customStart')}
                  minDate={filters.customEnd ? new Date(new Date(filters.customEnd).getTime() - 90*24*60*60*1000) : undefined}
                  maxDate={filters.customEnd || undefined}
                />
              </div>
              <div>
              <ModernDatePicker
                placeholder="End Date"
                value={filters.customEnd}
                onChange={(e)=>handleQuickFilterChange(e, 'customEnd')}
                minDate={filters.customStart || undefined}
                maxDate={filters.customStart ? new Date(new Date(filters.customStart).getTime() + 90*24*60*60*1000) : undefined}
              />
              </div>
            </div>
          )}
          <div className="flex gap-4 mb-4 mt-4 flex-wrap">
        {Object.keys(filters).map(
          (key) => {
            // Skip customStart and customEnd if time is not "Custom Range"
            if ((key === 'customStart' || key === 'customEnd') && filters.time !== 'Custom Range') {
              return null;
            }
            
            return filters[key] && (
              <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                <span className="capitalize text-indigo-600">{key} - {filters[key]}</span>
                {key!=='time' && key!=='mode' && <button
                  className="ml-2 text-gray-600"
                  onClick={() => setFilters((prev) => ({ ...prev, [key]: '' }))}
                >
                  <FaTimes />
                </button>}
              </div>
            );
          }
        )}
      </div>
      <div className="flex gap-4 mb-4 mt-1 flex-wrap">
        {Object.entries(advancedFilters).map(([key, val]) => {
          if (val === '' || val == null) return null;
          if (shouldHideCrmLeadCreatedAtChipKey(key)) return null;
          if (key === 'crm_update_status') return null;
          if (key === 'business_insights_multiple') {
            const validItems = Array.isArray(val) ? val.filter((item) => item?.business_insight_key && item?.business_insight_value) : [];
            if (validItems.length === 0) return null;
            return (
              <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
                <span className="capitalize text-indigo-600">Business Insights - {validItems.map((item) => `${item.business_insight_key}:${item.business_insight_value}`).join(', ')}</span>
              </div>
            );
          }
          if (key === 'agent_feedback_multiple') return null;
          return (
            <div key={key} className="flex items-center bg-indigo-50 p-2 rounded" style={{fontSize: "0.6rem", lineHeight: "0.25rem"}}>
              <span className="capitalize text-indigo-600">{key} - {String(val)}</span>
              <button
                className="ml-2 text-gray-600"
                onClick={() => setAdvancedFilters((prev) => ({ ...prev, [key]: Array.isArray(prev[key]) ? [] : '' }))}
              >
                <FaTimes />
              </button>
            </div>
          );
        })}
      </div>
      </div>
      <div className={`fixed top-0 right-0 h-full bg-white border-l shadow-lg w-96 transition-transform duration-300 transform ${showAdvancedFilters ? 'translate-x-0' : 'translate-x-full'}`} style={{zIndex: "99999999999999"}}>
        <div className='bg-gray-50' style={{padding: "20px 24px",display: "flex",alignItems: "center",justifyContent: "space-between", borderBottom: "1px solid #00000012", height: "4rem"}}>
          <h2 className="text-lg font-medium mb-0 text-xl font-semibold text-gray-900">Advanced Filters</h2>
          <button onClick={() => setShowAdvancedFilters(false)}>
            <FaTimes />
          </button>
        </div>
        <div style={{overflowY: "auto", height: "calc(100vh - 4rem)"}}>
          <div className="space-y-4" style={{padding: "15px 0", paddingRight: "20px", paddingLeft: "1.5rem"}}>
            <div>
              <label className="block text-sm font-medium mb-2">Customer ID</label>
              <input type="text" placeholder="Enter Customer ID" className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full" value={advancedFilters.customer_id} onChange={(e) => handleAdvancedInputChange(e, 'customer_id')} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Conversation Direction</label>
              <ModernTimeDropdown width='w-full' placeholder="Select Direction" value={advancedFilters.conversation_direction} onChange={(e) => handleAdvancedInputChange(e, 'conversation_direction')} options={dummyAdvancedFilterData.conversation_directions}/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Disconnect Type</label>
              <ModernTimeDropdown width='w-full' placeholder="Select Type" value={advancedFilters.disconnect_type} onChange={(e) => handleAdvancedInputChange(e, 'disconnect_type')} options={dummyAdvancedFilterData.disconnect_types}/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Disconnect Reason</label>
              <ModernTimeDropdown width='w-full' placeholder="Select Reason" value={advancedFilters.disconnect_reason} onChange={(e) => handleAdvancedInputChange(e, 'disconnect_reason')} options={dummyAdvancedFilterData.disconnect_reasons}/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Average Latency</label>
              <input type="text" placeholder="Enter Average Latency" className="px-2 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none global-input w-full" value={advancedFilters.average_latency} onChange={(e) => handleAdvancedInputChange(e, 'average_latency')} />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Campaign ID</label>
              <ModernTimeDropdown width='w-full' placeholder="Select Campaign ID" value={advancedFilters.campaign_id} onChange={(e) => handleAdvancedInputChange(e, 'campaign_id')} options={dummyAdvancedFilterData.campaign_ids}/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Campaign Batch ID</label>
              <ModernTimeDropdown width='w-full' placeholder="Select Campaign Batch ID" value={advancedFilters.campaign_batch_id} onChange={(e) => handleAdvancedInputChange(e, 'campaign_batch_id')} options={dummyAdvancedFilterData.campaign_batch_ids}/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Transfer Status</label>
              <ModernTimeDropdown width='w-full' placeholder="Select Transfer Status" value={advancedFilters.transfer_status} onChange={(e) => handleAdvancedInputChange(e, 'transfer_status')} options={dummyAdvancedFilterData.transfer_statuses}/>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Transfer Agent Answered</label>
              <ModernTimeDropdown width='w-full' placeholder="Select Transfer Agent Answered" value={advancedFilters.transfer_agent_answered} onChange={(e) => handleAdvancedInputChange(e, 'transfer_agent_answered')} options={dummyAdvancedFilterData.transfer_agent_answereds}/>
            </div>
            <div className="bg-gray-100" style={{display: "flex", flexDirection: "column", height: "70px", alignItems: "center", justifyContent: "center"}}>
              <div
                className={`sections-headings ${expandedSections.businessInsights ? "expanded" : ""}`}
                onClick={() => setExpandedSections((prev)=>({...prev, businessInsights: !prev.businessInsights}))}
              >
                <h2 className="section-title" style={{fontSize: "15px", fontWeight: "500", display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%"}}>
                  Business Insights {expandedSections.businessInsights ? <FaCaretDown /> : <FaCaretRight />}
                </h2>
              </div>
            </div>
            {expandedSections.businessInsights && (
              <div className='p-2 rounded-md'>
                {(advancedFilters.business_insights_multiple || []).map((item, index) => (
                  <div className='m-4 flex flex-col items-center p-2 border border-gray-200 rounded-md bg-gray-100' key={index}>
                    <div className='mb-2 w-full'>
                      <label className="block text-sm font-medium mb-6 flex items-center justify-between">Business Insights Parameter
                        <button className='px-2 py-1 rounded-md' onClick={() => setAdvancedFilters((prev) => ({ ...prev, business_insights_multiple: prev.business_insights_multiple.filter((_, i) => i !== index) }))}><FaTimes/></button>
                      </label>
                      <ModernTimeDropdown width='w-full' placeholder="Select Parameter" value={item.business_insight_key || ''} onChange={(e) => updateBusinessInsights(index, 'business_insight_key', e.target.value)} options={getBusinessInsightKeys()}/>
                    </div>
                    {(item.business_insight_key || '') !== '' && (
                      <div className="mb-2 w-full">
                        <label className="block text-sm font-medium mb-2">Parameter Value</label>
                        <ModernTimeDropdown width='w-full' placeholder="Select Value" value={item.business_insight_value || ''} onChange={(e) => updateBusinessInsights(index, 'business_insight_value', e.target.value)} options={getBusinessInsightValues(item.business_insight_key)} />
                      </div>
                    )}
                  </div>
                ))}
                <div className='flex items-center justify-end mx-4'>
                  <button className='bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1 rounded-md text-sm' onClick={() => setAdvancedFilters((prev) => ({ ...prev, business_insights_multiple: [...(prev.business_insights_multiple || []), {'business_insight_key': '', 'business_insight_value': null}] }))}>+ Add More</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>


      <div className="py-6">
        <div style={{width: "100%", height: "65vh", background: "white"}}>
          {
            loadingTrendLine && (
              <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
                <div className="loader"></div>
                <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
              </div>
            )
          }
          {
            !loadingTrendLine && 
              <TrendsLineWithMetrics 
                data={metricsDistribution} 
                metrics={metricsData}
                activeMetric={activeMetric} 
                setActiveMetric={setActiveMetric}
                formatDate={formatDate}
                formatTime={formatTime}
                title="Call Metrics"
                width="w-full"
                setShowDetails={handleShowCalls}
                useMetricTabs={true}
                defaultSmoothing='1'
              />
          }
          </div>
        </div>
        <div style={{ width: "100%", height: "65vh", display: "flex", gap: "1rem"}}>
          {loadingPie ? (
            <div className="flex flex-1 justify-center items-center" style={{ minHeight: "50vh", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{ textAlign: "center" }}>Loading <br /> Please Wait</div>
            </div>
          ) : (
            <>
              <PieChart2 pieData={disconnectReasonsData} onSegmentClick={(data)=>handleSegmentClick(data, 'disconnect_reason')}  width="w-full" colors={['#0088FE', '#00C49F', '#FFBB28', '#FF8042']} title="Disconnect Reason" handleDownload={handleDownload} />
              <PieChart2 pieData={disconnectTypesData} onSegmentClick={(data)=>handleSegmentClick(data, 'disconnect_type')}  width="w-full" colors={['#0088FE', '#00C49F', '#FFBB28', '#FF8042']} title="Disconnect Type" handleDownload={handleDownload} />
            </>
          )}
        </div>
        
        <div style={{ width: "100%", height: "65vh", display: "flex", gap: "1rem", marginTop: "1rem"}}>
          {loadingPie ? (
            <div className="flex flex-1 justify-center items-center" style={{ minHeight: "50vh", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{ textAlign: "center" }}>Loading <br /> Please Wait</div>
            </div>
          ) : (
            <>
              <PieChart2 pieData={transferStatusData} onSegmentClick={(data)=>handleSegmentClick(data, 'transfer_status')}  width="w-full" colors={['#0088FE', '#00C49F', '#FFBB28', '#FF8042']} title="Transfer Status" handleDownload={handleDownload} />
              <PieChart2 pieData={transferAgentAnsweredData} onSegmentClick={(data)=>handleSegmentClick(data, 'transfer_agent_answered')}  width="w-full" colors={['#0088FE', '#00C49F', '#FFBB28', '#FF8042']} title="Transfer Agent Answered" handleDownload={handleDownload} />
            </>
          )}
        </div>
        <div style={{ width: "100%", height: "65vh", display: "flex", justifyContent: "space-between", gap: "1rem", marginTop: "1rem"}}>
          {loadingPie ? (
            <div className="flex flex-1 w-full justify-center items-center" style={{ minHeight: "50vh", flexDirection: "column" }}>
              <div className="loader"></div>
              <div style={{ textAlign: "center" }}>Loading <br /> Please Wait</div>
            </div>
          ) : (
            <>
          <PieChart2 pieData={crmUpdateStatusData} onSegmentClick={(data)=>handleSegmentClick(data, 'crm_update_status')}  width="w-1/2" colors={['#0088FE', '#00C49F', '#FFBB28', '#FF8042']} title="CRM Update Status" handleDownload={handleDownload} />
            <div className="flex flex-col w-1/2 h-full min-h-0 bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="flex items-start justify-between px-4 pt-4 pb-2 flex-shrink-0 gap-3">
                <h2 className="text-xl font-semibold text-gray-800 truncate">Slot Wise Connectivity</h2>
                <div className="flex items-center gap-2 flex-wrap justify-end flex-shrink-0">
                  {CONNECTED_SLOT_OPTIONS.map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setConnectedSlotType(opt.key)}
                      className={`px-2 py-1 text-xs rounded border ${connectedSlotType === opt.key ? 'bg-gray-500 text-white border-gray-500' : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 min-h-0 px-2 pb-3">
                <DualAxisBarLineChart
                  data={connectedSlotChartRows}
                  xAxisKey="slot_start"
                  xAxisLabel="Slot Time"
                  y1Key="connected_calls_percentage"
                  y2Key="total_calls"
                  y1Name="Connected Calls %"
                  y2Name="Total Calls"
                  y1AxisLabel="Connected Calls %"
                  y2AxisLabel="Total Calls"
                  y1Domain={[0, 100]}
                  histogramBars
                  tooltipLabelFormatter={(label, payload) => payload?.[0]?.payload?.slot_key ?? label}
                  height="100%"
                  width="w-full"
                />
              </div>
            </div>
            </>
          )}
        </div>
        <MetricsDistributionTable
          title="Call Details Metrics Distribution"
          tableData={callDetailsMetricsTable}
          loading={loadingPie}
        />
        <MetricsDistributionTable
          title="Customer Details Metrics Distribution"
          tableData={customerDetailsMetricsTable}
          loading={loadingPie}
        />
        <h2 className="text-xl font-semibold my-4">Business Insights</h2>
        {loadingBusinessInsights && (
          <div className="flex justify-center items-center" style={{ height: "88%", flexDirection: "column" }}>
            <div className="loader"></div>
            <div style={{textAlign: "center"}}>Loading <br/> Please Wait</div>
          </div>
        )}
        {!loadingBusinessInsights && businessInsightsData && (
          <PieChartsGrid data={businessInsightsData} />
        )}
        {showDetails && (
          <div style={{position: "fixed", left: "0%", top: "0%", width: "100%", height: "100%", background: "rgba(0, 0, 0, 0.5)", zIndex: "1001"}}>
            <div style={{position: "fixed", left: "2%", top: "4%", width: "96%", height: "92%", background: "white", borderRadius: "10px", boxShadow: "rgba(0, 0, 0, 0.05) 0px 1px 3px", 
            zIndex: "999999999"}}>
              <div style={{position: "absolute", right: "10px", top: "10px", cursor: "pointer"}} onClick={()=>{ setShowDetails(false); setSelectedSegment(null); }}><FaTimes style={{color: "slategray"}}/></div>
              <div style={{overflow: "auto", height: "100%", borderRadius: "10px"}}>
                <ConversationRecords type="modal-open" serviceID={service_id} clientID={client_id} segment={selectedSegment} />
              </div>
          </div>
        </div>
        )
      }
      <style jsx>
          {`
          .sections-headings {
                          cursor: pointer;
                          display: flex;
                          justify-content: space-between;
                          align-items: center;
                          padding: 15px;
                          border-radius: 10px;
                          transition: background 0.3s;
                          width: 100%;
                          }

                          .section-title {
                          font-size: 15px;
                          font-weight: 500;
                          display: flex;
                          align-items: center;
                          }
          `}
        </style>
      </div>
  );
};

export default HomePage;
